import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LayoutDashboard, ShoppingBag, LogOut, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import NotificationBell from "@/components/NotificationBell";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
    <div className="h-9 w-9 rounded-xl bg-[#0EA5E9] flex items-center justify-center shadow-lg shadow-sky-500/30 transition-transform group-hover:rotate-6">
      <img src="/phoenix-mark.png" alt="Fedeys" className="h-6 w-6 object-contain" />
    </div>
    <span className="font-display text-2xl font-900 font-extrabold tracking-tight text-[#0F172A]">
      Fedeys
    </span>
  </Link>
);

export default function Layout() {
  const { t, lang, toggle } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isSeller = user && (user.role === "seller" || user.role === "admin");
  const isAdmin = user && user.role === "admin";

  const navClass = ({ isActive }) =>
    `text-sm font-semibold transition-colors hover:text-[#0EA5E9] ${
      isActive ? "text-[#0EA5E9]" : "text-[#475569]"
    }`;

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="App min-h-screen bg-[#F9FAFB]">
      <header className="sticky top-0 z-50 glass border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden md:flex items-center gap-7">
            <NavLink to="/explore" className={navClass} data-testid="nav-explore">{t("nav.explore")}</NavLink>
            <NavLink to="/categories" className={navClass} data-testid="nav-categories">{t("nav.categories")}</NavLink>
            {isSeller && <NavLink to="/dashboard" className={navClass} data-testid="nav-dashboard">{t("nav.dashboard")}</NavLink>}
            {isAdmin && <NavLink to="/admin" className={navClass} data-testid="nav-admin">{t("nav.admin")}</NavLink>}
            {user && <NavLink to="/favorites" className={navClass} data-testid="nav-favorites">{t("nav.favorites")}</NavLink>}
            {user && <NavLink to="/purchases" className={navClass} data-testid="nav-purchases">{t("nav.purchases")}</NavLink>}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={toggle} data-testid="lang-toggle"
              className="text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] rounded-full px-3 py-1.5 hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors">
              {lang === "es" ? "ES · EN" : "EN · ES"}
            </button>
            {user ? (
              <div className="flex items-center gap-2">
                <NotificationBell />
                {!isSeller && (
                  <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")}
                    data-testid="become-seller-btn" className="rounded-full">
                    <Store className="h-4 w-4 mr-1" /> {t("nav.sell")}
                  </Button>
                )}
                <Button onClick={doLogout} size="sm" variant="ghost" data-testid="logout-btn" className="rounded-full">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate("/login")}
                  data-testid="login-btn" className="rounded-full font-semibold">{t("nav.login")}</Button>
                <Button size="sm" onClick={() => navigate("/register")} data-testid="register-btn"
                  className="rounded-full bg-[#0F172A] hover:bg-[#0EA5E9] text-white font-semibold">
                  {t("nav.register")}
                </Button>
              </>
            )}
          </div>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-[#E2E8F0] bg-white px-5 py-4 flex flex-col gap-3">
            <Link to="/explore" onClick={() => setOpen(false)} className="font-semibold text-[#0F172A]">{t("nav.explore")}</Link>
            <Link to="/categories" onClick={() => setOpen(false)} className="font-semibold text-[#0F172A]">{t("nav.categories")}</Link>
            {isSeller && <Link to="/dashboard" onClick={() => setOpen(false)} className="font-semibold text-[#0F172A]">{t("nav.dashboard")}</Link>}
            {user && <Link to="/favorites" onClick={() => setOpen(false)} className="font-semibold text-[#0F172A]">{t("nav.favorites")}</Link>}
            {user && <Link to="/purchases" onClick={() => setOpen(false)} className="font-semibold text-[#0F172A]">{t("nav.purchases")}</Link>}
            <button onClick={toggle} className="text-left text-sm font-bold uppercase text-[#0EA5E9]">{lang === "es" ? "English" : "Español"}</button>
            {user ? (
              <Button onClick={doLogout} variant="outline" className="rounded-full">{t("nav.logout")}</Button>
            ) : (
              <div className="flex gap-2">
                <Button onClick={() => { setOpen(false); navigate("/login"); }} variant="outline" className="rounded-full flex-1">{t("nav.login")}</Button>
                <Button onClick={() => { setOpen(false); navigate("/register"); }} className="rounded-full flex-1 bg-[#0F172A] text-white">{t("nav.register")}</Button>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="min-h-[70vh]">
        <Outlet />
      </main>

      <footer className="border-t border-[#E2E8F0] bg-white mt-24">
        <div className="max-w-7xl mx-auto px-5 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <Logo />
            <p className="text-[#475569] text-sm mt-4 max-w-sm leading-relaxed">{t("home.subtitle")}</p>
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <span className="font-bold uppercase tracking-widest text-xs text-[#94A3B8] mb-1">Marketplace</span>
            <Link to="/explore" className="text-[#475569] hover:text-[#0EA5E9]">{t("nav.explore")}</Link>
            <Link to="/categories" className="text-[#475569] hover:text-[#0EA5E9]">{t("nav.categories")}</Link>
            <Link to="/help" className="text-[#475569] hover:text-[#0EA5E9]">{t("nav.help")}</Link>
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <span className="font-bold uppercase tracking-widest text-xs text-[#94A3B8] mb-1">Fedeys</span>
            <Link to="/register" className="text-[#475569] hover:text-[#0EA5E9]">{t("nav.becomeSeller")}</Link>
            <Link to="/help" className="text-[#475569] hover:text-[#0EA5E9]">{t("nav.help")}</Link>
          </div>
        </div>
        <div className="border-t border-[#E2E8F0] py-5 text-center text-xs text-[#94A3B8]">© 2026 Fedeys — Digital products marketplace</div>
      </footer>
      <Toaster position="top-center" richColors />
    </div>
  );
}
